// src/components/Banking.jsx

import { motion } from "framer-motion";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useEffect, useRef, useState } from "react";
import {
    FaBolt,
    FaCoins,
    FaExchangeAlt,
    FaGlobe, FaHandHoldingUsd,
    FaLock,
    FaPiggyBank,
    FaRocket,
    FaShieldAlt,
    FaStore, FaUserPlus,
    FaUsers,
    FaWallet
} from "react-icons/fa";
import { TiArrowRight, TiArrowUp } from "react-icons/ti";
import { useNavigate } from "react-router-dom";
import AnimatedTitle from "./AnimatedTitle";
import Footer from './Footer';
import ScrollDownButton from './ScrollDownButton';
import GlassButton from './ui/GlassButton';
import { SparklesCore } from './ui/SparklesCore';

gsap.registerPlugin(ScrollTrigger);

const Banking = () => {
  const [scrollDownClicked, setScrollDownClicked] = useState(false);
  const [isBottom, setIsBottom] = useState(false);
  const navigate = useNavigate();

  const sectionRefs = useRef([]);
  const scrollDownRef = useRef(null);
  const scrollTopRef = useRef(null);
  const statsRef = useRef(null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });

    sectionRefs.current.forEach((section, index) => {
      if (section) {
        gsap.fromTo(
          section,
          { opacity: 0, y: 50 },
          {
            opacity: 1,
            y: 0,
            duration: 1,
            delay: index * 0.2,
            scrollTrigger: {
              trigger: section,
              start: "top 80%",
            },
          }
        );
      }
    });

    if (scrollDownRef.current) {
      gsap.fromTo(
        scrollDownRef.current,
        { opacity: 0, y: -20 },
        { opacity: 1, y: 0, duration: 1 }
      );
    }

    if (scrollTopRef.current) {
      gsap.fromTo(
        scrollTopRef.current,
        { opacity: 0, y: 20 },
        {
          opacity: 1,
          y: 0,
          duration: 0.5,
          scrollTrigger: {
            trigger: scrollTopRef.current,
            start: "top bottom",
          },
        }
      );
    }
  }, []);

  const checkIfBottom = () => {
    if (
      window.innerHeight + window.scrollY >=
      document.body.offsetHeight - 200
    ) {
      setIsBottom(true);
    } else {
      setIsBottom(false);
    }
  };

  const scrollToContent = () => {
    const content = document.getElementById("content");
    if (content) {
      const contentOffsetTop = content.offsetTop;
      window.scrollTo({
        top: contentOffsetTop - 100,
        behavior: "smooth",
      });
    }
    setScrollDownClicked(true);
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    setScrollDownClicked(false);
  };

  useEffect(() => {
    window.addEventListener("scroll", checkIfBottom);
    return () => {
      window.removeEventListener("scroll", checkIfBottom);
    };
  }, []);

  // Stats data
  const stats = [
    { value: "£50M+", label: "Assets Under Management", icon: <FaCoins /> },
    { value: "150K+", label: "Active Users", icon: <FaUsers /> },
    { value: "99.9%", label: "Uptime Guarantee", icon: <FaBolt /> },
    { value: "256-bit", label: "Bank-Grade Encryption", icon: <FaLock /> },
  ];

  // Banking services with enhanced data
  const bankingServices = [
    {
      title: "Zero-Crypto Wallet",
      description: "Web 3.0 blockchain-based crypto wallet with multi-layered security, staking capabilities, and seamless asset management.",
      icon: <FaWallet className="text-2xl" />,
      color: "cyan",
      featured: true,
      link: "/wallet",
    },
    {
      title: "Global Payments",
      description: "Instant, low-cost cross-border transactions with rapid settlement times and minimal fees worldwide.",
      icon: <FaGlobe className="text-2xl" />,
      color: "blue",
    },
    {
      title: "DeFi Lending",
      description: "Access decentralised lending platforms with competitive rates and transparent, intermediary-free processes.",
      icon: <FaHandHoldingUsd className="text-2xl" />,
      color: "purple",
    },
    {
      title: "Asset Tokenisation",
      description: "Transform traditional assets into digital tokens for enhanced liquidity and fractional ownership.",
      icon: <FaCoins className="text-2xl" />,
      color: "emerald",
    },
    {
      title: "Compliance Suite",
      description: "Automated KYC/AML solutions ensuring secure and legitimate transactions with global regulatory compliance.",
      icon: <FaShieldAlt className="text-2xl" />,
      color: "orange",
      link: "/compliance",
    },
    {
      title: "Crypto Exchange",
      description: "Trade cryptocurrencies on our secure platform with competitive fees and advanced trading tools.",
      icon: <FaExchangeAlt className="text-2xl" />,
      color: "pink",
    },
    {
      title: "High-Yield Savings",
      description: "Earn competitive interest on your digital assets with our blockchain-enabled savings accounts.",
      icon: <FaPiggyBank className="text-2xl" />,
      color: "yellow",
    },
    {
      title: "Merchant Solutions",
      description: "Accept cryptocurrency payments seamlessly with low transaction fees and global reach.",
      icon: <FaStore className="text-2xl" />,
      color: "teal",
    },
  ];

  // Color schemes for cards
  const colorSchemes = {
    cyan: {
      gradient: "from-cyan-500/20 via-cyan-500/5 to-transparent",
      border: "hover:border-cyan-500/50",
      glow: "group-hover:shadow-cyan-500/25",
      icon: "text-cyan-400",
      accent: "bg-cyan-500",
    },
    blue: {
      gradient: "from-blue-500/20 via-blue-500/5 to-transparent",
      border: "hover:border-blue-500/50",
      glow: "group-hover:shadow-blue-500/25",
      icon: "text-blue-400",
      accent: "bg-blue-500",
    },
    purple: {
      gradient: "from-purple-500/20 via-purple-500/5 to-transparent",
      border: "hover:border-purple-500/50",
      glow: "group-hover:shadow-purple-500/25",
      icon: "text-purple-400",
      accent: "bg-purple-500",
    },
    emerald: {
      gradient: "from-emerald-500/20 via-emerald-500/5 to-transparent",
      border: "hover:border-emerald-500/50",
      glow: "group-hover:shadow-emerald-500/25",
      icon: "text-emerald-400",
      accent: "bg-emerald-500",
    },
    orange: {
      gradient: "from-orange-500/20 via-orange-500/5 to-transparent",
      border: "hover:border-orange-500/50",
      glow: "group-hover:shadow-orange-500/25",
      icon: "text-orange-400",
      accent: "bg-orange-500",
    },
    pink: {
      gradient: "from-pink-500/20 via-pink-500/5 to-transparent",
      border: "hover:border-pink-500/50",
      glow: "group-hover:shadow-pink-500/25",
      icon: "text-pink-400",
      accent: "bg-pink-500",
    },
    yellow: {
      gradient: "from-yellow-500/20 via-yellow-500/5 to-transparent",
      border: "hover:border-yellow-500/50",
      glow: "group-hover:shadow-yellow-500/25",
      icon: "text-yellow-400",
      accent: "bg-yellow-500",
    },
    teal: {
      gradient: "from-teal-500/20 via-teal-500/5 to-transparent",
      border: "hover:border-teal-500/50",
      glow: "group-hover:shadow-teal-500/25",
      icon: "text-teal-400",
      accent: "bg-teal-500",
    },
  };

  // Features for the bento section
  const keyFeatures = [
    {
      title: "Blockchain Security",
      description: "256-bit encryption with multi-signature authentication and cold storage for maximum asset protection.",
      icon: <FaShieldAlt />,
    },
    {
      title: "Instant Transactions",
      description: "Execute transactions in seconds, not days. Our blockchain infrastructure ensures lightning-fast processing.",
      icon: <FaBolt />,
    },
    {
      title: "Global Accessibility",
      description: "Bank from anywhere in the world, 24/7. No borders, no barriers, just seamless financial freedom.",
      icon: <FaGlobe />,
    },
  ];

  return (
    <div className="w-screen min-h-screen bg-black text-blue-50">
      {/* Hero Section - Full Viewport Height */}
      <div className="relative w-full h-screen flex flex-col items-center justify-center p-5">
        <div className="relative w-full max-w-6xl h-64 md:h-[500px] overflow-hidden rounded-[4rem] border border-white/10 z-10 bg-black shadow-[0_0_80px_rgba(34,211,238,0.15)]">
          <video
            src="/videos/banking-hero.mp4"
            autoPlay
            loop
            muted
            playsInline
            className="absolute left-0 top-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black" />
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <SparklesCore
              id="bankingHeroSparkles"
              background="transparent"
              minSize={0.4}
              maxSize={1.0}
              particleDensity={30}
              particleColor="#22d3ee"
              className="absolute inset-0"
            />
            <AnimatedTitle
              title="Banking Reimagined"
              containerClass="text-center text-blue-50 font-zentry relative z-10"
            />
            <motion.p 
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="text-cyan-400/80 font-bold tracking-[0.4em] uppercase text-[10px] mt-6 z-10"
            >
              Institutional Grade · Borderless Finance · 24/7 Access
            </motion.p>
          </div>
        </div>
      </div>

      {/* Scroll Down Button */}
      {!scrollDownClicked && (
        <ScrollDownButton
          ref={scrollDownRef}
          additionalClasses=""
          onClick={scrollToContent}
        />
      )}

      {/* Content Section */}
      <div id="content" className="px-6 md:px-10 py-20 mx-auto max-w-7xl">
        
        {/* Stats Section */}
        <motion.div 
          ref={statsRef}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-20"
        >
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
                className="relative group"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/20 to-blue-500/20 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <div className="relative p-6 bg-black/50 backdrop-blur-sm border border-white/10 rounded-2xl text-center group-hover:border-cyan-500/30 transition-all duration-500">
                  <div className="text-cyan-400 text-2xl mb-3 flex justify-center">{stat.icon}</div>
                  <div className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">
                    {stat.value}
                  </div>
                  <div className="text-sm text-gray-300 mt-2">{stat.label}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Section Title */}
        <div className="mb-16 text-center" ref={(el) => (sectionRefs.current[0] = el)}>
          <motion.h2 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-4xl md:text-5xl font-bold"
          >
            <span className="bg-gradient-to-r from-cyan-400 via-blue-400 to-purple-400 bg-clip-text text-transparent">
              Our Banking Solutions
            </span>
          </motion.h2>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="mt-4 text-gray-300 max-w-2xl mx-auto"
          >
            Empowering your financial journey with innovative blockchain banking services
          </motion.p>
        </div>

        {/* Bento Grid Layout */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-20" ref={(el) => (sectionRefs.current[1] = el)}>
          {bankingServices.map((service, index) => {
            const colors = colorSchemes[service.color];
            const isLarge = service.featured || index === 0;
            
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05, duration: 0.5 }}
                className={`group relative ${isLarge ? 'md:col-span-2 md:row-span-2' : ''}`}
              >
                {/* Animated border gradient */}
                <div className="absolute -inset-[1px] rounded-2xl bg-gradient-to-r from-white/10 via-white/5 to-white/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                
                {/* Outer glow */}
                <div className={`absolute -inset-2 rounded-2xl blur-xl opacity-0 ${colors.glow} group-hover:opacity-30 transition-opacity duration-500`} />
                
                {/* Card */}
                <div className={`relative h-full bg-black/80 backdrop-blur-sm rounded-2xl p-6 border border-white/10 ${colors.border} transition-all duration-500 overflow-hidden`}>
                  {/* Spotlight effect */}
                  <div className={`absolute top-0 right-0 w-40 h-40 bg-gradient-radial ${colors.gradient} rounded-full blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />
                  
                  {/* Icon */}
                  <div className={`relative z-10 w-12 h-12 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center mb-4 ${colors.icon} group-hover:scale-110 transition-transform duration-300`}>
                    {service.icon}
                  </div>
                  
                  {/* Content */}
                  <h3 className="relative z-10 text-xl font-bold text-white mb-3">{service.title}</h3>
                  <p className={`relative z-10 text-gray-300 ${isLarge ? 'text-base' : 'text-sm'} leading-relaxed`}>
                    {service.description}
                  </p>
                  
                  {/* Link button */}
                  {service.link && (
                    <GlassButton
                      onClick={() => navigate(service.link)}
                      className="relative z-10 mt-4"
                      variant={service.color}
                      size="sm"
                    >
                      <div className="flex items-center gap-2">
                         Explore <TiArrowRight className="text-lg" />
                      </div>
                    </GlassButton>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Key Features Section */}
        <div className="mb-20" ref={(el) => (sectionRefs.current[2] = el)}>
          <motion.h3
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-3xl font-bold text-center mb-12"
          >
            <span className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">
              Why Choose Zero-Bank?
            </span>
          </motion.h3>
          
          <div className="grid md:grid-cols-3 gap-8">
            {keyFeatures.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="group relative"
              >
                {/* Moving border effect */}
                <div className="absolute -inset-[1px] rounded-2xl overflow-hidden">
                  <motion.div
                    className="absolute inset-0 bg-gradient-to-r from-cyan-500 via-blue-500 to-purple-500"
                    animate={{ rotate: 360 }}
                    transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
                    style={{ transformOrigin: "center" }}
                  />
                </div>
                
                <div className="relative bg-black rounded-2xl p-8 h-full">
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-cyan-500/20 to-blue-500/20 flex items-center justify-center text-2xl text-cyan-400 mb-6">
                    {feature.icon}
                  </div>
                  <h4 className="text-xl font-bold text-white mb-3">{feature.title}</h4>
                  <p className="text-gray-300 leading-relaxed">{feature.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="relative rounded-3xl overflow-hidden"
          ref={(el) => (sectionRefs.current[3] = el)}
        >
          {/* Background gradient */}
          <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/20 via-blue-500/20 to-purple-500/20" />
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
          
          {/* Content */}
          <div className="relative z-10 py-16 px-8 text-center">
            <motion.div
              initial={{ scale: 0.9 }}
              whileInView={{ scale: 1 }}
              viewport={{ once: true }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-cyan-500/20 border border-cyan-500/30 text-cyan-400 text-sm mb-6"
            >
              <FaRocket /> Get Started Today
            </motion.div>
            
            <h3 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Ready to Experience the Future of Banking?
            </h3>
            <p className="text-gray-300 max-w-2xl mx-auto mb-8">
              Join thousands of users who have already transformed their financial journey with Zero-Bank's revolutionary blockchain banking platform.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <GlassButton
                onClick={() => window.open('https://zero-bank.xyz', '_blank')}
                className="px-8 py-4"
                variant="cyan"
                size="lg"
              >
                <div className="flex items-center gap-2">
                   <FaUserPlus /> Open Account
                </div>
              </GlassButton>
              <GlassButton
                onClick={() => navigate('/wallet')}
                className="px-8 py-4"
                variant="gray"
                size="lg"
              >
                <div className="flex items-center gap-2">
                   <FaWallet /> Explore Wallet
                </div>
              </GlassButton>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Back to Top Button */}
      {isBottom && (
        <div
          ref={scrollTopRef}
          className="fixed bottom-10 left-5 flex items-center gap-3 px-5 py-3 bg-black/40 backdrop-blur-sm border border-cyan-500/30 rounded-full shadow-lg shadow-cyan-500/10 cursor-pointer hover:bg-black/60 hover:border-cyan-400/50 transition-all duration-500 z-50"
          onClick={scrollToTop}
          aria-label="Back to Top"
        >
          <TiArrowUp className="text-cyan-400 transition-all duration-500" size={24} />
          <span className="relative z-20 text-sm uppercase text-white/80 font-medium tracking-wider transition-colors duration-500 hover:text-white">
            BACK TO TOP
          </span>
        </div>
      )}
    
      {/* Footer */}  
      <Footer />
    </div>
  );
};

export default Banking;
